import { HD_BODYGRAPH_CHANNELS } from './hd-bodygraph-metadata';
import { HdBodygraphRenderStateFactory } from './hd-bodygraph-render-state.factory';
import { HdBodygraphCenter, HdBodygraphConfig, HdBodygraphPlanetaryActivation } from './hd-bodygraph.models';
import {
  HdCycleChannelVm,
  HdCycleGateActivationVm,
  HdCycleHangingGateVm,
  hdActivationsByGate,
} from './hd-cycle-keynotes.builder';
import { HD_CYCLE_CENTER_CONDITIONING_COLUMNS } from './hd-cycles-cheat-sheet-metadata';
import {
  HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON,
  HdPlanetKey,
  getHdPlanetDisplayConfig,
} from './planet-grid/hd-planet-display-config';

/**
 * Oorsprong van een kaart-activatie op de I Tjing-lijnkaart: `transit` = de
 * cyclus/het moment/de partner (groene transit-stijl), `chart` = de eigen
 * chart (zwart/rood per zijde). Zelfde begrippen als `HdGateLineActivationOrigin`.
 */
export type HdCycleCardOrigin = 'chart' | 'transit';

/** Eén planeetactivatie waarvoor een I Tjing-lijnkaart hoort (planeet op `poort.lijn`). */
export interface HdCycleCardSource {
  readonly planetId: number;
  readonly planetKey: HdPlanetKey;
  readonly glyph: string;
  readonly gate: number;
  readonly line: number;
  readonly side: 'personality' | 'design';
  readonly origin: HdCycleCardOrigin;
}

/**
 * Plek van een kaart in een opsomming. `first` = de eerste keer dat deze
 * activatie voorkomt (render-volgorde): daar staat de volledige kaart; latere
 * plekken tonen een verwijzing naar {@link anchorId} (DOM-id van de kaart).
 * Zonder dedup (Geavanceerd-paneel, items zijn apart uitklapbaar) is elke plek `first`.
 */
export interface HdCycleCardSlot {
  readonly source: HdCycleCardSource;
  readonly anchorId: string;
  readonly first: boolean;
}

/** Ongedefinieerd center dat door de combinatie definitie krijgt, met de activaties van de ander die dat doen. */
export interface HdCycleConditionedCenter {
  readonly center: HdBodygraphCenter;
  readonly causes: readonly HdCycleCardSource[];
}

/** Volgorde-index per planetId, conform de planetengrid (zon eerst). */
const PLANET_ORDER: ReadonlyMap<number, number> = new Map(
  HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON.map((config, index) => [config.planetId, index]),
);

/** Stabiel DOM-id van de volledige kaart van een activatie. */
export function hdCycleCardAnchorId(source: HdCycleCardSource): string {
  return `cycle-card-${source.origin}-${source.side}-${source.planetId}-${source.gate}-${source.line}`;
}

export function hdCyclePlanetOrder(planetId: number): number {
  return PLANET_ORDER.get(planetId) ?? 99;
}

/** Kaart-bronnen voor de activaties op één poort (cycle-/composite-chips → kaarten). */
export function hdCycleCardSourcesForGate(
  gate: number,
  activations: readonly HdCycleGateActivationVm[],
  origin: HdCycleCardOrigin,
): HdCycleCardSource[] {
  return activations.map(activation => ({
    planetId: getHdPlanetDisplayConfig(activation.planetKey).planetId,
    planetKey: activation.planetKey,
    glyph: activation.glyph,
    gate,
    line: activation.line,
    side: activation.side,
    origin,
  }));
}

/** Planeetprioriteit (planetengrid-volgorde), dan personality vóór design, dan poortnummer. */
export function sortHdCycleCardSources(sources: readonly HdCycleCardSource[]): HdCycleCardSource[] {
  return [...sources].sort((left, right) =>
    hdCyclePlanetOrder(left.planetId) - hdCyclePlanetOrder(right.planetId)
    || (left.side === right.side ? 0 : left.side === 'personality' ? -1 : 1)
    || left.gate - right.gate);
}

/** Laagste planeetindex van een set kaarten (99 zonder kaarten) — de prioriteit van het item. */
export function hdCycleCardPriority(sources: readonly HdCycleCardSource[]): number {
  return sources.reduce((lowest, source) => Math.min(lowest, hdCyclePlanetOrder(source.planetId)), 99);
}

/**
 * Items van een sectie op planeetprioriteit (zon eerst): een kanaal of poort
 * lift mee op zijn hoogste planeet; gelijke prioriteit houdt de aangeleverde
 * volgorde (kanaallijst / poortnummer). Zelfde regel in het Just Now-paneel en
 * de secties van het Geavanceerd-paneel (cycle/moment/composite, Steven 2026-10-14).
 */
export function sortByHdCyclePriority<T>(
  items: readonly T[],
  sourcesOf: (item: T) => readonly HdCycleCardSource[],
): T[] {
  return items
    .map((item, index) => ({ item, index, priority: hdCycleCardPriority(sourcesOf(item)) }))
    .sort((left, right) => left.priority - right.priority || left.index - right.index)
    .map(({ item }) => item);
}

/**
 * Kaarten van een kanaal-item: de activaties van de cyclus/het moment/de
 * partner op de poorten die zij activeert (electromagnetic = één poort,
 * compromis/dominantie/vernieuwing = twee). Met `includeNatal` (composite:
 * beide personen brengen poorten) ook de natal-kant als `chart`-kaarten.
 */
export function buildHdCycleChannelCardSources(
  channel: Pick<HdCycleChannelVm, 'gates'>,
  options: { readonly includeNatal?: boolean } = {},
): HdCycleCardSource[] {
  const sources = channel.gates.flatMap(side => [
    ...hdCycleCardSourcesForGate(side.gate, side.cycleActivations, 'transit'),
    ...(options.includeNatal ? hdCycleCardSourcesForGate(side.gate, side.natalActivations, 'chart') : []),
  ]);
  return sortHdCycleCardSources(sources);
}

/** Kaarten van een hangende (Omgevend-)poort: één per planeetactivatie. */
export function buildHdCycleHangingGateCardSources(hanging: HdCycleHangingGateVm): HdCycleCardSource[] {
  return sortHdCycleCardSources(hdCycleCardSourcesForGate(hanging.gate, hanging.cycleActivations, 'transit'));
}

/**
 * Bronnen → kaartplekken. Met een `seen`-set (dedup over een hele opsomming,
 * Just Now-paneel) krijgt enkel het eerste voorkomen van een activatie de
 * volledige kaart; zonder set is elke plek een volledige kaart.
 */
export function toHdCycleCardSlots(
  sources: readonly HdCycleCardSource[],
  seen?: Set<string>,
): HdCycleCardSlot[] {
  return sources.map(source => {
    const anchorId = hdCycleCardAnchorId(source);
    if (seen == null) {
      return { source, anchorId, first: true };
    }
    const first = !seen.has(anchorId);
    seen.add(anchorId);
    return { source, anchorId, first };
  });
}

/**
 * Niet-zelf centers: centers die in `ownConfiguration` ongedefinieerd zijn en
 * in de combinatie met `otherActivations` definitie krijgen, in
 * cheat-sheet-volgorde, elk met als oorzaken de activaties van de ander op de
 * (in de combinatie gevormde) kanalen die het center raken. Chiron telt nooit mee.
 */
export function buildHdCycleConditionedCenters(
  ownConfiguration: HdBodygraphConfig,
  otherActivations: readonly HdBodygraphPlanetaryActivation[],
  renderStateFactory: HdBodygraphRenderStateFactory,
  origin: HdCycleCardOrigin = 'transit',
): HdCycleConditionedCenter[] {
  if (otherActivations.length === 0) {
    return [];
  }
  const ownState = renderStateFactory.create(ownConfiguration);
  const combinedState = renderStateFactory.create({
    ...ownConfiguration,
    activations: [...ownConfiguration.activations, ...otherActivations],
  });
  const otherByGate = hdActivationsByGate(otherActivations);
  return HD_CYCLE_CENTER_CONDITIONING_COLUMNS.flat()
    .filter(center => !ownState.centerStates[center].isDefined && combinedState.centerStates[center].isDefined)
    .map(center => {
      const causes = HD_BODYGRAPH_CHANNELS
        .filter(channel => channel.centers.includes(center) && (combinedState.channelStates[channel.key]?.isDefined ?? false))
        .flatMap(channel => channel.gates.flatMap(gate => hdCycleCardSourcesForGate(gate, otherByGate.get(gate) ?? [], origin)));
      const unique = [...new Map(causes.map(cause => [hdCycleCardAnchorId(cause), cause])).values()];
      return { center, causes: sortHdCycleCardSources(unique) };
    });
}
